import { GetServerSideProps } from "next";
import { NextSeo, NextSeoProps } from "next-seo";

import { useCategoryTopicList } from "repositories/hooks/useCategoryTopicList";
import { useRouter } from "next/router";
import Layout from "components/layout";
import { ReactNode } from "react";
import { MediaHeader } from "features/media/components/atoms/MediaHeader";
import { repository } from "repositories";
import getNextSeoProps from "utils/ssr/getNextSeoProps";
import { handleServerSideError } from "shared/utils/ssr";
import { ColumnListLayout } from "features/media/components/template/ColumnListLayout";
import { TopicCategory } from "shared/types";
import { MediaNav } from "features/media/components/atoms/MediaNav";

interface Props {
  category: TopicCategory;
  categories: TopicCategory[];
  nextSeoProps: NextSeoProps;
}

const PressCategoryColumnListPage = ({
  category,
  categories,
  nextSeoProps,
}: Props) => {
  const { query } = useRouter();

  const currentPage = Number(query.page || 1);

  const { topics, meta, isLoading } = useCategoryTopicList(query.alias, {
    page: currentPage,
  });

  return (
    <>
      <NextSeo {...nextSeoProps} />
      <ColumnListLayout
        title={category.name}
        topics={topics}
        categories={categories}
        currentPage={currentPage}
        totalPage={meta.totalPages}
        isLoading={isLoading}
      />
    </>
  );
};

export default PressCategoryColumnListPage;

PressCategoryColumnListPage.getLayout = (page: ReactNode) => {
  return (
    <Layout header={<MediaHeader />} nav={null}>
      <MediaNav />
      {page}
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { params, query } = ctx;

  if (!params.alias) {
    return {
      notFound: true,
    };
  }

  try {
    const categories = await (
      await repository.topicCategory.getList("press").data()
    ).categories;
    const category = categories.find(
      (category) => category.alias === params.alias
    );

    const currentPage = Number(query.page || 1);
    const title = `${
      currentPage > 1
        ? `(${currentPage}ページ目) ${category.name}`
        : category.name
    }に関するまとめ`;
    const description = `このページではMIROR PRESSに掲載されている「${category.name}」に関するまとめ記事をたくさんご紹介しています。MIROR PRESSで「${category.name}」に関するまとめ記事をお楽しみください。`;
    const url = `https://miror.jp/press/column-category/${category.alias}`;

    const nextSeoProps = getNextSeoProps(ctx, {
      title,
      description,
      url,
      options: {
        additionalLinkTags: [{ rel: "icon", href: "/favicon.ico" }],
        openGraph: {
          title,
          description,
          url,
          type: "article",
          site_name: "ミラープレス",
        },
        nofollow: currentPage > 1,
        noindex: currentPage > 1,
      },
    });

    return {
      props: {
        category,
        categories,
        nextSeoProps,
      },
    };
  } catch (error) {
    return handleServerSideError(error);
  }
};
